/**
 * A sync-run status, shown as icon + word + tone.
 *
 * THREE signals, never one. Colour alone leaves the difference between a failed
 * and a successful run invisible to a reader who cannot separate the two hues —
 * and on this page that difference is the whole point. The icon and the German
 * word carry it; the colour only reinforces.
 *
 * `syncStatusTone` is the shared mapping from `bexioFormat`, so this page and the
 * overview preview agree on which states are good, bad and in-flight. The WORD
 * comes from this page's own map (see `bexioSyncRunsView`).
 */
import React from "react";
import { CheckCircle2, CircleAlert, Loader2, MinusCircle } from "lucide-react";

import { syncStatusTone } from "../../../components/admin/bexio/bexioFormat";
import { statusLabel } from "./bexioSyncRunsView";

const ICON = {
  good: CheckCircle2,
  bad: CircleAlert,
  warn: Loader2,
  neutral: MinusCircle,
};

const PILL = {
  good:
    "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-400",
  bad:
    "border-red-200 bg-red-50 text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300",
  warn:
    "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300",
  neutral: "border-border bg-muted text-muted-foreground",
};

export default function SyncStatusPill({ status }) {
  const tone = syncStatusTone(status);
  const Icon = ICON[tone] || ICON.neutral;
  return (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-medium ${
        PILL[tone] || PILL.neutral
      }`}
    >
      <Icon
        aria-hidden="true"
        className={`h-3.5 w-3.5 shrink-0 ${status === "running" ? "animate-spin" : ""}`}
      />
      {statusLabel(status)}
    </span>
  );
}
